import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  canMoveStep, canMoveTacticToStep, getSourceGroupUpdate,
  groupTacticsByActionOrder,
  TacticActionOrderGroup,
} from '../TacticKanban.utils';
import { errorToast } from 'basics/utils/toast';
import { minimumAllowedNumberOfTacticsInGroup } from 'components/TacticForm/TacticForm.constants';
import { Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';

type UseTacticKanbanReorderingParams = {
  groupedTactics: TacticActionOrderGroup;
  idToStepMap: Map<string, string>;
  shouldRestrictNewGroupCreationForApprovedStartedCep: boolean;
  shouldRestrictAddingToExistingGroupForApprovedStartedCep: boolean;
  setReorderingInProgress: (value: boolean) => void;
  setReorderingInProgressRef: (value: boolean) => void;
  updateGroupRelations: (newTactics: Tactic[], originalTactics?: Tactic[]) => Promise<boolean>;
  syncAfterMutations: (successMessage: string) => Promise<void>;
  handleTacticGroupCreation: (tacticIdsToGroup: string[], groupIdToReuse?: string | null) => Promise<Tactic>;
};

const useTacticKanbanReordering = ({
  groupedTactics,
  idToStepMap,
  shouldRestrictNewGroupCreationForApprovedStartedCep,
  shouldRestrictAddingToExistingGroupForApprovedStartedCep,
  setReorderingInProgress,
  setReorderingInProgressRef,
  updateGroupRelations,
  syncAfterMutations,
  handleTacticGroupCreation,
}: UseTacticKanbanReorderingParams) => {
  const { t } = useTranslation();

  const rollbackTacticsState = (originalTactics: Tactic[], originalTacticGroups: Tactic[]) => {
    cepState.tactics = originalTactics;
    cepState.tacticGroups = originalTacticGroups;
  };

  const clearDependenciesToReleasedGroup = (
    tactics: Tactic[],
    releasedGroupId: string | null,
    excludedTacticIds: string[] = [],
  ) => {
    if (!releasedGroupId) {
      return tactics;
    }

    return tactics.map((tactic) => {
      const shouldKeepDependency = excludedTacticIds.includes(tactic._id);
      const dependsOnReleasedGroup = tactic.previousTactic?._id === releasedGroupId;

      if (shouldKeepDependency || !dependsOnReleasedGroup) {
        return tactic;
      }

      return {
        ...tactic,
        previousTactic: null,
      };
    });
  };

  const reindexAfterDeletion = async (deletedTacticId: string) => {
    setReorderingInProgress(true);
    setReorderingInProgressRef(true);

    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const deletedTactic = originalTactics.find((tactic) => tactic._id === deletedTacticId);
      const deletedGroupId = deletedTactic?.parentTactic?._id || null;

      const remainingTactics = originalTactics.filter((tactic) => tactic._id !== deletedTacticId);

      if (deletedGroupId) {
        const remainingTacticsInGroup = remainingTactics.filter(
          (tactic) => tactic.parentTactic?._id === deletedGroupId,
        );

        if (remainingTacticsInGroup.length < minimumAllowedNumberOfTacticsInGroup) {
          cepState.tacticGroups = cepState.tacticGroups.filter((group) => group._id !== deletedGroupId);

          remainingTactics.forEach((tactic, index) => {
            if (tactic.parentTactic?._id === deletedGroupId) {
              remainingTactics[index] = {
                ...tactic,
                parentTactic: null,
              };
            }
          });
        }
      }

      const groupedByOrder = groupTacticsByActionOrder(remainingTactics);
      const orderedStepKeys = Object.keys(groupedByOrder).sort((a, b) => +a - +b);

      const reIndexedTactics: Tactic[] = [];

      orderedStepKeys.forEach((originalActionOrderKey, index) => {
        const newActionOrder = index + 1;

        groupedByOrder[originalActionOrderKey].forEach((tactic) => {
          reIndexedTactics.push({
            ...tactic,
            actionOrder: newActionOrder,
          });
        });
      });

      const updatedTactics = clearDependenciesToReleasedGroup(
        reIndexedTactics,
        deletedGroupId,
      );

      cepState.tactics = updatedTactics;

      const hasError = await updateGroupRelations(reIndexedTactics, originalTactics);

      if (hasError) {
        errorToast(t('errors_standard'));
        rollbackTacticsState(originalTactics, originalTacticGroups);
        return;
      }

      await syncAfterMutations(t('maya_action_canceling_tactic_success'));
    } catch (error) {
      errorToast(t('errors_standard'));
      rollbackTacticsState(originalTactics, originalTacticGroups);
    } finally {
      setReorderingInProgress(false);
      setReorderingInProgressRef(false);
    }
  };

  const reorderSteps = async (sourceIndex: number, destinationIndex: number) => {
    setReorderingInProgress(true);

    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const orderedStepKeys = Object.keys(groupedTactics).sort((a, b) => +a - +b);
      const movedStepKey = orderedStepKeys[sourceIndex];

      const movementCheck = canMoveStep({
        movedStepKey,
        destinationIndex,
        orderedStepKeys,
        originalTactics,
        idToStepMap,
        dependencyBeforeMessage: t('maya_tactic_step_reorder_dependency_error_before'),
        dependencyAfterMessage: t('maya_tactic_step_reorder_dependency_error_after'),
      });

      if (!movementCheck.allowed) {
        errorToast(movementCheck.message ?? t('maya_tactic_step_reorder_error'));
        return;
      }

      const [movedStepKeySafe] = orderedStepKeys.splice(sourceIndex, 1);
      orderedStepKeys.splice(destinationIndex, 0, movedStepKeySafe);

      const newTacticsForState: Tactic[] = [];

      orderedStepKeys.forEach((originalActionOrderKey, index) => {
        const newActionOrder = index + 1;

        originalTactics
          .filter((tactic) => tactic.actionOrder === parseInt(originalActionOrderKey, 10))
          .forEach((tactic) => {
            newTacticsForState.push({
              ...tactic,
              actionOrder: newActionOrder,
            });
          });
      });

      cepState.tactics = newTacticsForState;

      const hasError = await updateGroupRelations(newTacticsForState, originalTactics);

      if (hasError) {
        errorToast(t('maya_tactic_step_reorder_error'));
        rollbackTacticsState(originalTactics, originalTacticGroups);
        return;
      }

      await syncAfterMutations(t('maya_tactic_step_reorder_success'));
    } catch (error) {
      errorToast(t('maya_tactic_step_reorder_error'));
      rollbackTacticsState(originalTactics, originalTacticGroups);
    } finally {
      setReorderingInProgress(false);
    }
  };

  const setStepMandatory = async (currentStep: string, isMandatory: boolean) => {
    setReorderingInProgress(true);

    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const currentOrder = parseInt(currentStep, 10);

      const newTactics = cepState.tactics.map((tactic) => {
        if (tactic.actionOrder === currentOrder) {
          return {
            ...tactic,
            mandatory: isMandatory,
          };
        }

        return tactic;
      });

      cepState.tactics = newTactics;

      const hasError = await updateGroupRelations(newTactics, originalTactics);

      if (hasError) {
        errorToast(t('maya_tactic_mandatory_update_error'));
        rollbackTacticsState(originalTactics, originalTacticGroups);
        return;
      }

      await syncAfterMutations(t('maya_tactic_mandatory_update_success'));
    } catch (error) {
      errorToast(t('maya_tactic_mandatory_update_error'));
      rollbackTacticsState(originalTactics, originalTacticGroups);
    } finally {
      setReorderingInProgress(false);
    }
  };

  const setStepDependency = async (currentStep: string, dependsOnStep: string | null) => {
    setReorderingInProgress(true);

    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      let newPreviousTactic: Tactic | null = null;

      if (dependsOnStep) {
        const prevStepOrder = parseInt(dependsOnStep, 10);
        const prevStepTactics = cepState.tactics.filter((tactic) => tactic.actionOrder === prevStepOrder);

        if (prevStepTactics.length > 0) {
          const prevGroup = prevStepTactics.find((tactic) => tactic.parentTactic)?.parentTactic;
          newPreviousTactic = prevGroup || prevStepTactics[prevStepTactics.length - 1];
        }
      }

      const currentOrder = parseInt(currentStep, 10);
      const tacticsToUpdate = cepState.tactics.filter((tactic) => tactic.actionOrder === currentOrder);

      if (tacticsToUpdate.length === 0) {
        setReorderingInProgress(false);
        return;
      }

      const newTactics = cepState.tactics.map((tactic) => {
        if (tactic.actionOrder === currentOrder) {
          return {
            ...tactic,
            previousTactic: newPreviousTactic,
          };
        }

        return tactic;
      });

      cepState.tactics = newTactics;

      const hasError = await updateGroupRelations(newTactics, originalTactics);

      if (hasError) {
        errorToast(t('maya_tactic_dependency_update_error'));
        rollbackTacticsState(originalTactics, originalTacticGroups);
        return;
      }

      await syncAfterMutations(t('maya_tactic_dependency_update_success'));
    } catch (error) {
      errorToast(t('maya_tactic_dependency_update_error'));
      rollbackTacticsState(originalTactics, originalTacticGroups);
    } finally {
      setReorderingInProgress(false);
    }
  };

  const moveTacticToStep = async (draggableId: string, destinationId: string, destinationIndex: number) => {
    setReorderingInProgress(true);
    setReorderingInProgressRef(true);

    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const originalDestinationActionOrder = parseInt(destinationId, 10);
      const tacticToMoveOriginal = originalTactics.find((tactic) => tactic._id === draggableId);

      if (!tacticToMoveOriginal) {
        return;
      }

      const movementCheck = canMoveTacticToStep({
        draggedTactic: tacticToMoveOriginal,
        destinationActionOrder: originalDestinationActionOrder,
        destinationIndex,
        originalTactics,
        idToStepMap,
        dependencyErrorMessage: t('maya_tactic_step_reorder_dependency_error'),
      });

      if (!movementCheck.allowed) {
        errorToast(
          movementCheck.message ?? t('maya_tactic_move_error'),
        );
        return;
      }

      const { tacticsToUngroup, groupToDeleteId } = getSourceGroupUpdate(draggableId, originalTactics);
      const tacticsInDestinationBeforeMove = originalTactics.filter(
        (tactic) => tactic.actionOrder === originalDestinationActionOrder,
      );
      const existingGroupInDestination = tacticsInDestinationBeforeMove.find((tactic) => tactic.parentTactic)?.parentTactic;
      const referenceTacticInDestination = tacticsInDestinationBeforeMove.length > 0
        ? tacticsInDestinationBeforeMove[0]
        : null;

      const movedTactic = cloneDeep(tacticToMoveOriginal);

      movedTactic.parentTactic = existingGroupInDestination || null;

      if (referenceTacticInDestination) {
        movedTactic.mandatory = referenceTacticInDestination.mandatory;
        movedTactic.previousTactic = existingGroupInDestination
          ? referenceTacticInDestination.previousTactic || null
          : null;
      } else {
        movedTactic.previousTactic = null;
      }

      const tacticsWithoutMoved = cloneDeep(cepState.tactics).filter((tactic) => tactic._id !== draggableId);

      tacticsToUngroup.forEach((sibling) => {
        const index = tacticsWithoutMoved.findIndex((tactic) => tactic._id === sibling._id);

        if (index > -1) {
          tacticsWithoutMoved[index].parentTactic = null;
        }
      });

      const tacticsByOriginalActionOrder = tacticsWithoutMoved.reduce<Record<string, Tactic[]>>((acc, tactic) => {
        if (typeof tactic.actionOrder !== 'number') {
          return acc;
        }

        const key = String(tactic.actionOrder);

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(tactic);

        return acc;
      }, {});

      const orderedOriginalActionOrders = Object.keys(tacticsByOriginalActionOrder)
        .map(Number)
        .sort((a, b) => a - b);

      const destinationStepIndex = orderedOriginalActionOrders.findIndex(
        (actionOrder) => actionOrder === originalDestinationActionOrder,
      );

      const steps: Tactic[][] = orderedOriginalActionOrders.map(
        (actionOrder) => tacticsByOriginalActionOrder[String(actionOrder)],
      );

      if (destinationStepIndex === -1) {
        steps.push([movedTactic]);
      } else {
        const destinationStep = steps[destinationStepIndex];
        const safeDestinationIndex = Math.min(destinationIndex, destinationStep.length);

        destinationStep.splice(safeDestinationIndex, 0, movedTactic);
      }

      let destinationActionOrder = 1;

      const newTactics: Tactic[] = steps.flatMap((stepTactics, stepIndex) => {
        const newActionOrder = stepIndex + 1;

        if (stepTactics.some((tactic) => tactic._id === draggableId)) {
          destinationActionOrder = newActionOrder;
        }

        return stepTactics.map((tactic): Tactic => ({
          ...tactic,
          actionOrder: newActionOrder,
        }));
      });

      const destinationTactics = newTactics.filter((tactic) => tactic.actionOrder === destinationActionOrder);

      const shouldCreateNewGroup = !existingGroupInDestination
          && destinationTactics.every((tactic) => !tactic.parentTactic)
          && destinationTactics.length === 2;

      const shouldMoveToExistingGroup = Boolean(existingGroupInDestination);

      const isNewGroupCreationRestricted = shouldRestrictNewGroupCreationForApprovedStartedCep
          && shouldCreateNewGroup;

      const isAddingToExistingGroupRestricted = shouldRestrictAddingToExistingGroupForApprovedStartedCep
          && shouldMoveToExistingGroup;

      if (isNewGroupCreationRestricted) {
        errorToast(t('maya_tactic_new_group_creation_restricted_for_approved_started_cep'));
        return;
      }

      if (isAddingToExistingGroupRestricted) {
        errorToast(t('maya_tactic_adding_to_existing_group_restricted_for_approved_started_cep'));
        return;
      }

      const tacticIdsToGroup = shouldCreateNewGroup
        ? destinationTactics.map((tactic) => tactic._id)
        : [];

      let finalTactics: Tactic[] = newTactics;

      if (shouldCreateNewGroup) {
        const newGroup = await handleTacticGroupCreation(tacticIdsToGroup, groupToDeleteId);

        finalTactics = finalTactics.map((tactic) => {
          if (!tacticIdsToGroup.includes(tactic._id)) {
            return tactic;
          }

          return {
            ...tactic,
            parentTactic: newGroup,
            actionOrder: destinationActionOrder,
            previousTactic: null,
          };
        });
      }

      finalTactics = clearDependenciesToReleasedGroup(
        finalTactics,
        groupToDeleteId,
        tacticIdsToGroup,
      );

      cepState.tactics = finalTactics;

      if (groupToDeleteId) {
        cepState.tacticGroups = cepState.tacticGroups.filter((group) => group._id !== groupToDeleteId);
      }

      const hasError = await updateGroupRelations(finalTactics, originalTactics);

      if (hasError) {
        errorToast(t('maya_tactic_move_error'));
        rollbackTacticsState(originalTactics, originalTacticGroups);
        return;
      }

      await syncAfterMutations(t('maya_tactic_move_success'));
    } catch (error) {
      errorToast(t('maya_tactic_move_error'));
      rollbackTacticsState(originalTactics, originalTacticGroups);
    } finally {
      setReorderingInProgress(false);
      setReorderingInProgressRef(false);
    }
  };

  const createStepFromTactic = async (draggableId: string, placeholderId: string) => {
    setReorderingInProgress(true);
    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const targetPrevStepKey = placeholderId.replace('create-step-after-', '');
      const insertAfterOrder = parseInt(targetPrevStepKey, 10);
      const draggedTacticOriginal = originalTactics.find((tactic) => tactic._id === draggableId);
      if (!draggedTacticOriginal) return;

      const sourceGroupId = draggedTacticOriginal.parentTactic?._id;
      let tacticsToUngroup: Tactic[] = [];
      let groupToDeleteId: string | null = null;

      if (sourceGroupId) {
        const siblings = originalTactics
          .filter((tactic) => tactic.parentTactic?._id === sourceGroupId && tactic._id !== draggableId);
        if (siblings.length < 2) {
          groupToDeleteId = sourceGroupId;
          tacticsToUngroup = siblings;
        }
      }

      const remainingTactics = originalTactics.filter((tactic) => tactic._id !== draggableId);
      const groups: Record<string, Tactic[]> = {};
      remainingTactics.forEach((tactic) => {
        const order = String(tactic.actionOrder);
        if (!groups[order]) groups[order] = [];
        groups[order].push(tactic);
      });

      const sortedStepKeys = Object.keys(groups).sort((a, b) => +a - +b);
      const existingSteps: Tactic[][] = sortedStepKeys.map((key) => groups[key]);

      let insertionIndex = 0;
      existingSteps.forEach((step) => {
        if (step.length > 0 && typeof step[0].actionOrder === 'number' && step[0].actionOrder <= insertAfterOrder) {
          insertionIndex += 1;
        }
      });

      const dependencyErrorMessage = t('maya_tactic_step_reorder_dependency_error');

      const movementCheck = canMoveTacticToStep({
        draggedTactic: draggedTacticOriginal,
        destinationActionOrder: insertionIndex + 1,
        destinationIndex: insertionIndex,
        isCreatingNewStep: true,
        originalTactics,
        idToStepMap,
        dependencyErrorMessage,
      });

      if (!movementCheck.allowed) {
        errorToast(
          movementCheck.message || dependencyErrorMessage,
        );
        return;
      }

      const newStep = [draggedTacticOriginal];
      const newStepList = [...existingSteps];
      newStepList.splice(insertionIndex, 0, newStep);

      const reIndexedTactics: Tactic[] = [];
      newStepList.forEach((stepTactics, stepIndex) => {
        const newActionOrder = stepIndex + 1;
        stepTactics.forEach((tactic) => {
          const isDraggedTactic = String(tactic._id) === String(draggableId);
          const updatedTactic = {
            ...tactic,
            actionOrder: newActionOrder,
            mandatory: isDraggedTactic ? draggedTacticOriginal.mandatory : tactic.mandatory,
          };
          if (isDraggedTactic) {
            updatedTactic.parentTactic = null;
          } else if (tacticsToUngroup.some((tu) => String(tu._id) === String(updatedTactic._id))) {
            updatedTactic.parentTactic = null;
          }
          reIndexedTactics.push(updatedTactic);
        });
      });

      const updatedTactics = clearDependenciesToReleasedGroup(
        reIndexedTactics,
        groupToDeleteId,
        [draggableId],
      );

      cepState.tactics = updatedTactics;
      if (groupToDeleteId) {
        cepState.tacticGroups = cepState.tacticGroups.filter((g) => g._id !== groupToDeleteId);
      }

      const hasError = await updateGroupRelations(reIndexedTactics, originalTactics);
      if (hasError) {
        errorToast(t('maya_tactic_new_step_creation_error'));
        cepState.tactics = originalTactics;
        cepState.tacticGroups = originalTacticGroups;
        return;
      }

      await syncAfterMutations(t('maya_tactic_new_step_creation_success'));
    } catch (error) {
      errorToast(t('maya_tactic_new_step_creation_error'));
      cepState.tactics = originalTactics;
      cepState.tacticGroups = originalTacticGroups;
    } finally {
      setReorderingInProgress(false);
    }
  };

  const createStepFromTacticAtStart = async (draggableId: string) => {
    setReorderingInProgress(true);
    setReorderingInProgressRef(true);
    const originalTactics = cloneDeep(cepState.tactics);
    const originalTacticGroups = cloneDeep(cepState.tacticGroups);

    try {
      const draggedTacticOriginal = originalTactics.find((tactic) => tactic._id === draggableId);
      if (!draggedTacticOriginal) return;

      const movementCheck = canMoveTacticToStep({
        draggedTactic: draggedTacticOriginal,
        destinationActionOrder: 1,
        destinationIndex: 0,
        isCreatingNewStep: true,
        originalTactics,
        idToStepMap,
        dependencyErrorMessage: t('maya_tactic_step_reorder_dependency_error'),
      });

      if (!movementCheck.allowed) {
        errorToast(
          movementCheck.message ?? t('maya_tactic_new_step_creation_error'),
        );
        return;
      }

      const { tacticsToUngroup, groupToDeleteId } = getSourceGroupUpdate(draggableId, originalTactics);
      const remainingTactics = originalTactics.filter((tactic) => tactic._id !== draggableId);

      const reIndexedTactics = remainingTactics.map((tactic) => {
        const updatedTactic = { ...tactic, actionOrder: (tactic.actionOrder || 0) + 1 };
        if (tacticsToUngroup.some((tu) => tu._id === tactic._id)) {
          updatedTactic.parentTactic = null;
        }
        return updatedTactic;
      });

      const newFirstStepTactic: Tactic = {
        ...draggedTacticOriginal,
        actionOrder: 1,
        parentTactic: null,
        previousTactic: null,
        mandatory: draggedTacticOriginal.mandatory,
      };

      const newTactics = clearDependenciesToReleasedGroup(
        [newFirstStepTactic, ...reIndexedTactics],
        groupToDeleteId,
        [draggableId],
      );
      cepState.tactics = newTactics;
      if (groupToDeleteId) {
        cepState.tacticGroups = cepState.tacticGroups.filter((g) => g._id !== groupToDeleteId);
      }

      const hasError = await updateGroupRelations(newTactics, originalTactics);
      if (hasError) {
        errorToast(t('maya_tactic_new_step_creation_error'));
        cepState.tactics = originalTactics;
        cepState.tacticGroups = originalTacticGroups;
        return;
      }

      await syncAfterMutations(t('maya_tactic_new_step_creation_success'));
    } catch (error) {
      errorToast(t('maya_tactic_new_step_creation_error'));
      cepState.tactics = originalTactics;
      cepState.tacticGroups = originalTacticGroups;
    } finally {
      setReorderingInProgress(false);
      setReorderingInProgressRef(false);
    }
  };

  return {
    reindexAfterDeletion,
    moveTacticToStep,
    createStepFromTactic,
    createStepFromTacticAtStart,
    reorderSteps,
    setStepMandatory,
    setStepDependency,
  };
};

export default useTacticKanbanReordering;
